// src/features/auth/login/Login.jsx
import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LoginHeader from './LoginHeader';
import SocialButtons from './SocialButtons';
import LoginForm from './LoginForm';
import { googleAuth } from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

const Login = ({ closeModal = () => { }, setIsLoginView = () => { } }) => {
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const navigate = useNavigate();
    const { loginWithBackend } = useAuth();
    const isGoogleAuthConfigured = Boolean(GOOGLE_CLIENT_ID);

    const handleCloseModal = () => {
        setIsPopupOpen(false);
        closeModal();
    };

    const handleLoginSuccess = () => {
        handleCloseModal();
        navigate('/dashboard');
    };

    const handleGoogleResponse = useCallback(async (response) => {
        try {
            const result = await googleAuth(response.credential);
            loginWithBackend(result.user, result.token);

            if (result.needs_completion) {
                toast.info('Completa tus datos para finalizar', { autoClose: 3000 });
                handleCloseModal();
                navigate(`/complete-registration/${result.persona_id}`);
                return;
            }

            toast.success('¡Sesión iniciada con Google!', { autoClose: 3000 });
            handleLoginSuccess();
        } catch (error) {
            toast.error(error.message || 'Error al iniciar sesión con Google', { autoClose: 5000 });
        } finally {
            setIsPopupOpen(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const signInWithGoogle = () => {
        if (isPopupOpen) return;

        if (!isGoogleAuthConfigured) {
            toast.error('Google Sign-In no configurado. Falta REACT_APP_GOOGLE_CLIENT_ID.', { autoClose: 5000 });
            return;
        }

        setIsPopupOpen(true);

        if (window.google?.accounts?.id) {
            window.google.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: handleGoogleResponse,
            });
            window.google.accounts.id.prompt((notification) => {
                if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
                    // Fallback: usar popup manual
                    window.google.accounts.oauth2.initCodeClient({
                        client_id: GOOGLE_CLIENT_ID,
                        scope: 'openid email profile',
                        callback: () => {},
                    });
                    // Usar el método de botón renderizado como fallback
                    const btn = document.createElement('div');
                    btn.id = '_g_signin_tmp';
                    btn.style.display = 'none';
                    document.body.appendChild(btn);
                    window.google.accounts.id.renderButton(btn, { type: 'icon' });
                    btn.querySelector('div[role=button]')?.click();
                    setTimeout(() => btn.remove(), 100);
                    setIsPopupOpen(false);
                }
            });
        } else {
            toast.error('Google Sign-In no disponible. Recarga la página.', { autoClose: 5000 });
            setIsPopupOpen(false);
        }
    };

    return (
        <div className="w-full max-w-md mx-auto">
            <div className="card overflow-hidden">
                <LoginHeader handleCloseModal={handleCloseModal} />
                <SocialButtons
                    signInWithGoogle={signInWithGoogle}
                    isPopupOpen={isPopupOpen}
                    isGoogleAuthConfigured={isGoogleAuthConfigured}
                />
                <div className="relative text-center px-6">
                    <div className="absolute inset-0 flex items-center px-6">
                        <div className="w-full border-t border-surface-200 dark:border-surface-700"></div>
                    </div>
                    <span className="relative bg-white dark:bg-surface-850 px-3 text-sm text-surface-400 dark:text-surface-500">
                        o ingresa tus credenciales
                    </span>
                </div>
                <LoginForm setIsLoginView={setIsLoginView} closeModal={handleCloseModal} onLoginSuccess={handleLoginSuccess} />
            </div>
        </div>
    );
};

export default Login;
